import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";

import { api, qs } from "@/api/client";
import type { Page } from "@/api/types";

export interface PickerValue { id: string; name: string }

interface Props {
  label: string;
  value: PickerValue | null;
  onChange: (value: PickerValue | null) => void;
  /** List endpoint, e.g. "/accounts". Must accept ?q= and return a Page. */
  endpoint: "/accounts" | "/contacts" | "/products";
  placeholder?: string;
  error?: string;
  /** Receives the full list item when one is chosen (for example a product's price). */
  onPickRaw?: (item: Record<string, unknown>) => void;
  /** Hide the visible label (the input keeps it as its accessible name). */
  hideLabel?: boolean;
  /** Extra filters for the list endpoint, e.g. { account_type: "dealer" }. */
  params?: Record<string, string>;
}

/** Accessible combobox that searches a list endpoint as you type. */
export function RecordPicker({ label, value, onChange, endpoint, placeholder = "Search…", error, onPickRaw, hideLabel, params }: Props) {
  const id = useId();
  const [text, setText] = useState(value?.name ?? "");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => { setText(value?.name ?? ""); }, [value]);
  useEffect(() => { const t = setTimeout(() => setQ(text), 200); return () => clearTimeout(t); }, [text]);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (!box.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const results = useQuery({
    queryKey: ["picker", endpoint, q, params],
    queryFn: () => api<Page<Record<string, unknown>>>("GET", `${endpoint}${qs({ q, limit: 8, ...(endpoint === "/products" ? { active: true } : {}), ...params })}`),
    enabled: open,
  });
  const raw = results.data?.items ?? [];
  const options: PickerValue[] = raw.map((r) => ({
    id: String(r.id), name: String(r.name ?? r.full_name ?? ""),
  }));

  const choose = (opt: PickerValue) => {
    onChange(opt); setText(opt.name); setOpen(false);
    const item = raw.find((r) => String(r.id) === opt.id);
    if (item && onPickRaw) onPickRaw(item);
  };

  return (
    <div ref={box} className="relative">
      <label htmlFor={id} className={hideLabel ? "sr-only" : "label"}>{label}</label>
      <div className="flex gap-1">
        <input
          id={id} role="combobox" aria-expanded={open} aria-controls={`${id}-list`} aria-autocomplete="list"
          className="input" placeholder={placeholder} value={text} autoComplete="off"
          onFocus={() => setOpen(true)}
          onChange={(e) => { setText(e.target.value); setOpen(true); setActive(0); if (!e.target.value) onChange(null); }}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, options.length - 1)); }
            if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
            if (e.key === "Enter" && open && options[active]) { e.preventDefault(); choose(options[active]); }
            if (e.key === "Escape") setOpen(false);
          }}
        />
        {value && (
          <button type="button" className="btn-ghost px-2" aria-label={`Clear ${label}`} onClick={() => { onChange(null); setText(""); }}>✕</button>
        )}
      </div>
      {open && (
        <ul id={`${id}-list`} role="listbox" className="absolute z-20 mt-1 max-h-60 w-full overflow-auto rounded-md border border-line bg-surface py-1 shadow-lg">
          {results.isLoading && <li className="px-3 py-2 text-sm text-muted">Searching…</li>}
          {!results.isLoading && options.length === 0 && <li className="px-3 py-2 text-sm text-muted">No matches</li>}
          {options.map((o, i) => (
            <li key={o.id} role="option" aria-selected={i === active}
              className={`cursor-pointer px-3 py-2 text-sm ${i === active ? "bg-brand-soft text-brand" : "hover:bg-canvas"}`}
              onMouseDown={(e) => { e.preventDefault(); choose(o); }} onMouseEnter={() => setActive(i)}>
              {o.name}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="field-error">{error}</p>}
    </div>
  );
}
